import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import SEO from './SEO';
import '../styles/VibrantCastHomepage.css';

const VibrantCastHomepage = () => {
  const [session, setSession] = useState(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        setSession(currentSession);
      } catch (error) {
        console.error('Error checking auth:', error);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  return (
    <div className="vibrantcast-homepage">
      <SEO 
        title="VibrantCast Curator Portal"
        description="Manage exhibitions, curate collections, and build immersive 3D gallery experiences with VibrantCast."
        image="/images/logo-120.png"
        keywords="art, artists, galleries, collectors, artwork, virtual galleries, art community, art discovery"
      />
      
      {/* Navigation */}
      <nav className="vc-navbar">
        <div className="vc-nav-container">
          <Link to="/" className="vc-logo">
            <img 
              src="/images/inline-logo-white-1.png" 
              alt="VibrantCast" 
              className="vc-logo-img"
            />
          </Link>
          
          <ul className="vc-nav-links">
            {/* Admin site - minimal nav */}
          </ul>
          
          <div className="vc-nav-buttons">
            {session ? (
              <Link to="/curator/dashboard" className="vc-btn-primary">
                Dashboard
              </Link>
            ) : (
              <Link to="/login" className="vc-btn-primary">Sign In</Link>
            )}
          </div>
        </div>
      </nav>

      {/* Video Hero Section */}
      <section className="vc-hero">
        <div className="vc-hero-video-container">
          <video
            className="vc-hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={handleVideoLoad}
            onError={(e) => console.error('Video error:', e)}
            poster="https://res.cloudinary.com/dh9jfogh1/image/upload/v1752451708/feature_graphic_2_1024_x_500_px_xkfffk.png"
          >
            <source src="https://res.cloudinary.com/dh9jfogh1/video/upload/v1776544316/vimeo-migrated/vimeo_1098256994.mp4" type="video/mp4" />
            <p style={{display: 'none'}}>Your browser does not support the video tag.</p>
          </video>
          
          {/* Video Overlay */}
          <div className="vc-hero-overlay"></div>
          
          {/* Hero Content */}
          <div className="vc-hero-content">
            <div className="vc-hero-container">
              <h1 className={`vc-hero-title ${isVideoLoaded ? 'loaded' : ''}`}>
                VibrantCast Curator Portal
              </h1>
              <p className={`vc-hero-subtitle ${isVideoLoaded ? 'loaded' : ''}`}>
                Manage exhibitions, curate collections, and build immersive 3D gallery experiences.
              </p>
              <div className={`vc-hero-cta ${isVideoLoaded ? 'loaded' : ''}`}>
                {session ? (
                  <Link to="/curator/dashboard" className="vc-btn-hero">
                    Go to Dashboard
                  </Link>
                ) : (
                  <Link to="/login" className="vc-btn-hero">
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curator Tools Section */}
      <section className="vc-functionality">
        <div className="vc-container">
          <div className="vc-section-header">
            <h2>Powerful Curation Tools</h2>
            <p>Everything you need to manage exhibitions and showcase art</p>
          </div>
          
          <div className="vc-features-grid">
            <div className="vc-feature-card">
              <h4>3D Gallery Builder</h4>
              <p>Design immersive virtual gallery spaces with walls, floors, and lighting controls.</p>
            </div>
            
            <div className="vc-feature-card">
              <h4>Collection Management</h4>
              <p>Organize artwork into collections with full metadata, pricing, and availability tracking.</p>
            </div>
            
            <div className="vc-feature-card">
              <h4>Artist Submissions</h4>
              <p>Streamlined submission workflow to review, approve, and onboard new artists.</p>
            </div>
            
            <div className="vc-feature-card">
              <h4>Event Management</h4>
              <p>Create and promote exhibitions, openings, and special events to your audience.</p>
            </div>
            
            <div className="vc-feature-card">
              <h4>AR Artwork Labels</h4>
              <p>Generate QR codes for in-gallery experiences linking to artist stories and purchase options.</p>
            </div>
            
            <div className="vc-feature-card">
              <h4>The Pulse Feed</h4>
              <p>Publish updates, new work, and announcements to the VibrantCast community.</p>
            </div>
          </div>
        </div>
      </section>

      {/* App Reference Section */}
      <section className="vc-app-download" id="download">
        <div className="vc-container">
          <div className="vc-download-content">
            <h2>Part of the VibrantCast Ecosystem</h2>
            <p>This curator portal connects to our full platform where artists, collectors, and art enthusiasts discover and experience art.</p>
            
            <div className="vc-download-buttons">
              <a 
                href="https://apps.apple.com/us/app/vibrantcast/id6569221167" 
                className="vc-store-button vc-app-store"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/images/apple.png" alt="Download on the App Store" />
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.mycompany.vibrantcast" 
                className="vc-store-button vc-google-play"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/images/google.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="vc-footer">
        <div className="vc-container">
          <div className="vc-footer-bottom">
            <p>© {new Date().getFullYear()} VibrantCast. All rights reserved.</p>
            <div className="vc-footer-links">
              <Link to="/terms">Terms of Service</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <a href="mailto:support@vibrantcast.com">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VibrantCastHomepage;